/**
 * client/src/lib/queryClient.ts — TanStack Query v5 client + apiRequest helper
 *
 * apiRequest is the single fetch wrapper used across all pages.
 * It includes credentials, parses JSON, and throws ApiError on non-2xx.
 */

import { QueryClient } from "@tanstack/react-query";

import { WRONG_ACTING_CONTEXT } from "@shared/acting-context";
import { isSessionDeathCode } from "@shared/session-death-codes";
import { POSSESSION_PROOF_REQUIRED } from "@shared/possession-proof";
import {
  LADDER_FLOOR_REQUIRED,
  isRetriableLadderFloorReason,
} from "@shared/ladder-floor-codes";
import { proveSessionPossession } from "@/lib/session-possession";
import { clearActiveKek } from "@/lib/vault-key";
import { clearConfirmCardDismissed } from "@/lib/memory-confirm-card-storage";

// ---------------------------------------------------------------------------
// Typed API error
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly body?: unknown,
  ) {
    super(message);
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

// ---------------------------------------------------------------------------
// Singleton QueryClient
// ---------------------------------------------------------------------------

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:     30_000,
      gcTime:        5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry auth errors — they require user action
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
        // Same reason (#2459): a read refused because it belongs to a DIFFERENT
        // acting context of the viewer's own is a settled answer about which hat
        // they are wearing. Keyed on the CODE, not the 409 status, so no other
        // conflict response changes behaviour.
        if (error instanceof ApiError && error.code === WRONG_ACTING_CONTEXT) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

// ---------------------------------------------------------------------------
// apiRequest — universal fetch helper
// ---------------------------------------------------------------------------

/** Per-call knobs that don't belong on the standard `RequestInit`. */
export interface ApiRequestConfig {
  /**
   * Abort (and reject) the request after this many ms. Omit for no timeout.
   * Used by bootstrap queries (e.g. `/api/auth/me`) so a hung connection — a
   * server restarted mid-flight, a dropped socket — SETTLES and can be retried,
   * rather than leaving the query pending forever (which strands the app on a
   * permanent loading spinner).
   *
   * Scope: the timer guards the fetch up to the response settling (the reported
   * failure mode is connection-level). It is cleared once headers arrive, so a
   * subsequent hung body stream (`res.json()`) is not separately timed. Not
   * combined with a caller-supplied `options.signal`: passing `timeoutMs` takes
   * over the signal.
   */
  timeoutMs?: number;
  /**
   * Never run the possession heartbeat for this call: a re-tap-fixable 403
   * propagates as an ordinary ApiError instead of prompting for a passkey.
   * For deferred / background effects (e.g. the day-2 delete commit, which
   * fires seconds after the tap, possibly on another page) where a context-
   * free passkey prompt would be worse than the call simply not happening.
   */
  noPossessionRetry?: boolean;
}

/**
 * Fetch a JSON API endpoint.
 * - Always sends cookies (credentials: "include")
 * - Throws ApiError with HTTP status + server error code on non-2xx
 * - Optionally aborts (and rejects) after `config.timeoutMs`
 * - On a 403 a fresh passkey tap can clear — POSSESSION_PROOF_REQUIRED, or
 *   LADDER_FLOOR_REQUIRED with a re-tap-fixable `reason` (see `isRetapFixable`)
 *   — runs the possession heartbeat (session-possession.ts) and retries the
 *   request exactly once
 */
export async function apiRequest<T = unknown>(
  url:     string,
  options?: RequestInit,
  config?: ApiRequestConfig,
): Promise<T> {
  try {
    return await sendRequest<T>(url, options, config);
  } catch (err) {
    // Possession heartbeat (ledger #119). The server refused this read because
    // the session cannot show a recent WebAuthn possession proof. Run the
    // ceremony and retry ONCE. The retry calls `sendRequest` rather than
    // `apiRequest`, so the single retry is STRUCTURAL: a server that answers
    // POSSESSION_PROOF_REQUIRED again cannot drive a passkey-prompt loop — the
    // second 403 propagates to the caller as an ordinary ApiError.
    //
    // A cancelled prompt rejects here with the ceremony's own error rather than
    // the original 403. That is the honest report: the read did not fail
    // because of the server, it failed because the check was not completed.
    //
    // SAFE TO REPLAY, including for a non-idempotent request. The gate is
    // Express middleware mounted BEFORE the route handler, so a refusal it
    // issues means the handler never ran and nothing was written — the retry
    // is a first attempt, not a second. That property is structural and must
    // hold for any future gated POST: the gate must never be applied INSIDE a
    // handler that has already had an effect.
    if (config?.noPossessionRetry || !(err instanceof ApiError) || !isRetapFixable(err)) throw err;
    await proveSessionPossession();
    return await sendRequest<T>(url, options, config);
  }
}

/**
 * Is this refusal one the SAME heartbeat can clear (AC-22, spec module 04)?
 *
 * Two codes reach this point, and only two:
 *
 *   - `POSSESSION_PROOF_REQUIRED` (ledger #119) — always yes; the gate exists
 *     to ask for exactly this proof.
 *   - `LADDER_FLOOR_REQUIRED` — yes ONLY when the server's machine-readable
 *     `reason` is one a tap can actually change (`shared/ladder-floor-codes.ts`
 *     owns that list and documents why `level_too_low` is not on it). The most
 *     common ladder refusal in practice IS a Settled band or an absent stamp,
 *     which is the identical remedy — before this, that case surfaced as a bare
 *     403 with no offered way to satisfy it, even though the app already knew
 *     how to fix it.
 *
 * Deliberately reason-gated rather than code-gated: prompting for a passkey on
 * a refusal a passkey cannot fix (not enough enrolment, a context-stamp
 * mismatch) is a dead end dressed up as an action, and it costs the holder a
 * biometric prompt to learn nothing.
 *
 * The single-retry and safe-to-replay properties documented above hold
 * unchanged for this code: `requireLadderFloor` is Express middleware mounted
 * BEFORE the route handler, so a refusal it issues means the handler never ran
 * and nothing was written.
 */
function isRetapFixable(err: ApiError): boolean {
  if (err.code === POSSESSION_PROOF_REQUIRED) return true;
  if (err.code !== LADDER_FLOOR_REQUIRED) return false;
  return isRetriableLadderFloorReason((err.body as Record<string, unknown> | null)?.reason);
}

async function sendRequest<T>(
  url:     string,
  options?: RequestInit,
  config?: ApiRequestConfig,
): Promise<T> {
  const { timeoutMs } = config ?? {};
  const controller = timeoutMs !== undefined ? new AbortController() : undefined;
  const timeoutId =
    controller !== undefined
      ? setTimeout(
          () =>
            controller.abort(
              new DOMException("Request timed out", "TimeoutError"),
            ),
          timeoutMs,
        )
      : undefined;

  let res: Response;
  try {
    res = await fetch(url, {
      credentials: "include",
      ...options,
      // Only override the signal when a timeout is requested; otherwise a
      // caller-supplied `options.signal` (spread above) is preserved untouched.
      ...(controller ? { signal: controller.signal } : {}),
    });
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const msg  = (body as Record<string, string>)?.error ?? res.statusText;
    const code = (body as Record<string, string>)?.code;
    // AC-48 / L0-9 / Rule 3 — the vault KEK is client-derived and lives ONLY in
    // this tab's memory; the server holds no copy to wipe when it deletes the
    // session rows. So the server telling us the session is dead is the only
    // signal that the buffer has outlived its session, and it must be zeroed
    // BEFORE the rejection reaches the caller — a caller that swallows the
    // error must not leave live key material sitting in the slot. Wiping is
    // idempotent and safe when no KEK is held (e.g. a signed-out visitor).
    if (res.status === 401 && isSessionDeathCode(code)) {
      clearActiveKek();
      // The login is over (timeout/eviction), so "Later" on the day-2 confirm
      // card — which lasts until the next sign-in — ends with it.
      clearConfirmCardDismissed();
    }
    throw new ApiError(res.status, msg, code, body);
  }

  return body as T;
}

/**
 * POST JSON shorthand.
 */
export function postJSON<T = unknown>(url: string, data: unknown): Promise<T> {
  return apiRequest<T>(url, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(data),
  });
}
